import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import request from "@/api";
import { useUserStore } from "@/stores/react/authStore";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { normalizeOperationActions } from "@/views/components/ProTable/components/TableColumn";
import type { Refund } from "@zhihaoscm/entity";
import {
    DateRangePicker as ZhDateRangePicker,
    Table as ZhTable,
    type TableColumnProps,
    Button as ZhFilterButton,
    Input as ZhFilterInput,
    Select as ZhFilterSelect
} from "zhihao-ui-react";
import { REFUND_STATE_LABELS, REFUND_STATE_OPTIONS } from "./refundState";

export interface TradeRefundListResult {
    list: Refund[];
    total: number;
}

export interface TradeRefundSummary {
    refundTotalAmount: number;
    refundedAmount: number;
}

export interface TradeRefundQuery {
    page: number;
    size: number;
    id?: string;
    orderId?: string;
    keyword?: string;
    states?: number[];
    beginTime?: string;
    endTime?: string;
    sortKey?: "refund_time" | "created_time";
    sortOrder?: "asc" | "desc";
}

export interface TradeRefundPageProps {
    loadRefunds?: (query: TradeRefundQuery) => Promise<TradeRefundListResult>;
    loadSummary?: () => Promise<TradeRefundSummary>;
    cancelRefund?: (id: number | string) => Promise<unknown>;
    previewRefundFile?: (id: number | string) => Promise<string | undefined>;
    permissions?: string[];
}

/** 将退款金额格式化为 Vue 页面相同的两位小数展示。 */
function formatMoney(value: number | undefined): string {
    return (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 将日期输入转换为 Vue 退款接口约定的当天起止时间。 */
function formatDateTime(value: string, endOfDay = false): string {
    return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`;
}

/** 将 zhihao 表格排序字段转换为旧 Vue 退款分页接口参数。 */
function getTradeRefundSortParams(sort: { prop?: string; order: "ascending" | "descending" | null }) {
    const sortKey =
        sort.prop === "refundTime" ? "refund_time" : sort.prop === "createdTime" ? "created_time" : undefined;
    if (!sortKey || !sort.order) return {};
    return { sortKey, sortOrder: sort.order === "ascending" ? "asc" : "desc" } as const;
}

/** 沿用 Vue `/admin/refund/paging` 分页接口。 */
export async function loadTradeRefunds(query: TradeRefundQuery): Promise<TradeRefundListResult> {
    const response = await request.service.get<TradeRefundListResult>("/admin/refund/paging", { params: query });
    return response.data;
}

/** 沿用 Vue `/admin/refund/amount` 退款统计接口。 */
export async function loadTradeRefundSummary(): Promise<TradeRefundSummary> {
    const response = await request.service.get<TradeRefundSummary>("/admin/refund/amount", { params: {} });
    return response.data;
}

/** 沿用 Vue 取消退款接口，删除操作由 Vue service 映射为 DELETE。 */
export async function cancelTradeRefund(id: number | string): Promise<unknown> {
    const response = await request.service.delete(`/admin/refund/${id}`);
    return response.data;
}

/** 查询 Vue 文件服务并返回退款凭证地址。 */
export async function previewTradeRefundFile(id: number | string): Promise<string | undefined> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${id}`);
    return response.data.path;
}

/** 将退款记录的采购企业映射为 Vue 列表中的用户文案。 */
function getCustomerLabel(row: Refund): string {
    const customer = row.customerEnterprise;
    return `${customer?.name || customer?.realName || ""}(${customer?.mobile || ""})`;
}

/** 交易管理退款列表首屏，保留 Vue 的统计卡片、筛选字段和核心表格列。 */
export function TradeRefundPage({
    loadRefunds = loadTradeRefunds,
    loadSummary = loadTradeRefundSummary,
    cancelRefund = cancelTradeRefund,
    previewRefundFile = previewTradeRefundFile,
    permissions
}: TradeRefundPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 路由直接进入列表时复用登录态权限；测试和嵌入场景传入 permissions 时保持显式覆盖。
    const effectivePermissions = permissions ?? storePermissions;
    const [draftFilters, setDraftFilters] = useState({
        id: "",
        orderId: "",
        keyword: "",
        states: [] as string[],
        beginDate: "",
        endDate: ""
    });
    const [query, setQuery] = useState<TradeRefundQuery>({ page: 1, size: 10 });
    const [items, setItems] = useState<Refund[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<TradeRefundSummary>({ refundTotalAmount: 0, refundedAmount: 0 });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const hasPermission = (permission: string) => effectivePermissions.includes(permission);

    const cancel = async (id: string) => {
        if (!(await confirmDialog("是否取消退款？"))) return;
        try {
            await cancelRefund(id);
            notifyOperationSuccess("已取消");
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("退款取消失败");
        }
    };

    const preview = async (id: number | string) => {
        try {
            const path = await previewRefundFile(id);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("退款凭证预览失败");
        }
    };

    const updateFilter = (key: keyof typeof draftFilters, value: string | string[]) => {
        setDraftFilters(current => ({ ...current, [key]: value }));
    };

    /** 清空退款列表筛选项，并恢复旧 Vue ProTable 的默认第一页。 */
    const resetFilters = () => {
        setDraftFilters({ id: "", orderId: "", keyword: "", states: [], beginDate: "", endDate: "" });
        setErrorMessage(null);
        setQuery({ page: 1, size: 10 });
    };

    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const beginDate = draftFilters.beginDate.trim();
        const endDate = draftFilters.endDate.trim();
        // 日期范围必须完整且有序，避免向 Vue 接口提交不可用条件。
        if (Boolean(beginDate) !== Boolean(endDate)) {
            setErrorMessage("请选择完整创建日期范围");
            return;
        }
        if (beginDate && endDate && beginDate > endDate) {
            setErrorMessage("创建开始日期不能晚于结束日期");
            return;
        }
        const nextQuery: TradeRefundQuery = { page: 1, size: query.size };
        if (draftFilters.id.trim()) nextQuery.id = draftFilters.id.trim();
        if (draftFilters.orderId.trim()) nextQuery.orderId = draftFilters.orderId.trim();
        if (draftFilters.keyword.trim()) nextQuery.keyword = draftFilters.keyword.trim();
        if (draftFilters.states.length) nextQuery.states = draftFilters.states.map(Number);
        if (beginDate && endDate) {
            nextQuery.beginTime = formatDateTime(beginDate);
            nextQuery.endTime = formatDateTime(endDate, true);
        }
        setErrorMessage(null);
        setQuery(nextQuery);
    };

    useEffect(() => {
        let disposed = false;
        void loadRefunds(query)
            .then(result => {
                if (!disposed) {
                    setItems(result.list || []);
                    setTotal(Number(result.total) || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("退款列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadRefunds, query]);

    useEffect(() => {
        let disposed = false;
        void loadSummary()
            .then(result => {
                if (!disposed) {
                    setSummary({
                        refundTotalAmount: Number(result.refundTotalAmount) || 0,
                        refundedAmount: Number(result.refundedAmount) || 0
                    });
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("退款统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadSummary]);

    const columns: TableColumnProps<Refund>[] = [
        {
            label: "退款编号",
            width: 170,
            prop: "id",
            // 保留旧 Vue 编号列的详情入口，便于从退款列表查看完整记录。
            render: ({ row }) => <Link to={`/tradeManage/refund/detail?id=${row.id}`}>{row.id}</Link>
        },
        {
            label: "关联订单",
            width: 170,
            prop: "orderId",
            render: ({ row }) =>
                row.orderId ? <Link to={`/tradeManage/tradeOrder/detail?id=${row.orderId}`}>{row.orderId}</Link> : "-"
        },
        { label: "采购用户", minWidth: 280, prop: "customer", render: ({ row }) => getCustomerLabel(row) },
        { label: "退款类型", minWidth: 106, prop: "refundType" },
        { label: "退款说明", minWidth: 160, prop: "remark", render: ({ row }) => row.remark || "--" },
        {
            label: "退款金额(元)",
            width: 130,
            prop: "amount",
            align: "right" as const,
            render: ({ row }) => formatMoney(row.amount)
        },
        {
            label: "退款时间",
            minWidth: 188,
            prop: "refundTime",
            sortable: true,
            render: ({ row }) => dateFormat(row.refundTime, DATETIME_PICKER_FORMAT)
        },
        {
            label: "创建时间",
            width: 170,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => dateFormat(row.createdTime, DATETIME_PICKER_FORMAT)
        },
        {
            label: "状态",
            minWidth: 92,
            prop: "state",
            render: ({ row }) => REFUND_STATE_LABELS[Number(row.state)] || "-"
        },
        {
            label: "操作",
            fixed: "right",
            width: 120,
            prop: "operation",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            render: ({ row }) =>
                normalizeOperationActions(
                    <Space>
                        {Number(row.state) === 1 && hasPermission("ROLE_REFUND_DEAL") ? (
                            <Button type="link" onClick={() => navigate(`/tradeManage/refund/audit?id=${row.id}`)}>
                                审核
                            </Button>
                        ) : null}
                        {Number(row.state) === 3 && hasPermission("ROLE_REFUND_MANAGE") ? (
                            <Button type="link" onClick={() => navigate(`/tradeManage/refund/edit?id=${row.id}`)}>
                                修改
                            </Button>
                        ) : null}
                        {[1, 3].includes(Number(row.state)) && hasPermission("ROLE_REFUND_DEAL") ? (
                            <Button type="link" onClick={() => void cancel(row.id)}>
                                取消
                            </Button>
                        ) : null}
                        {Number(row.state) === 4 && row.fileId ? (
                            <Button type="link" onClick={() => void preview(row.fileId)}>
                                预览
                            </Button>
                        ) : null}
                    </Space>
                )
        }
    ];

    return (
        <section aria-label="trade-refund-page" className="admin-trade-list-page flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable
                    columns={columns}
                    data={items}
                    onSortChange={sort =>
                        setQuery(current => ({ ...current, page: 1, ...getTradeRefundSortParams(sort) }))
                    }
                    pagination={false}
                    rowKey="id"
                    size="small"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                退款管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Space aria-label="退款金额统计" className="admin-trade-stat-cards" size={0}>
                                <Card className="admin-trade-stat-card" size="small">
                                    <Typography.Text className="admin-trade-stat-card__label">
                                        退款总金额（元）
                                    </Typography.Text>
                                    <Typography.Title className="admin-trade-stat-card__value" level={3}>
                                        {formatMoney(summary.refundTotalAmount)}
                                    </Typography.Title>
                                </Card>
                                <Card className="admin-trade-stat-card" size="small">
                                    <Typography.Text className="admin-trade-stat-card__label">
                                        已退款总金额（元）
                                    </Typography.Text>
                                    <Typography.Title
                                        className="admin-trade-stat-card__value admin-trade-stat-card__value--danger"
                                        level={3}
                                    >
                                        {formatMoney(summary.refundedAmount)}
                                    </Typography.Title>
                                </Card>
                            </Space>

                            <form className="admin-table-filter-form mb-4" onSubmit={submitFilters}>
                                {hasPermission("ROLE_REFUND_DEAL") ? (
                                    <ZhFilterButton
                                        aria-label="新增"
                                        className="admin-table-primary-action"
                                        type="primary"
                                        onClick={() => navigate("/tradeManage/refund/add")}
                                    >
                                        新增
                                    </ZhFilterButton>
                                ) : null}
                                <label className="trade-refund-filter-id">
                                    退款编号
                                    <ZhFilterInput
                                        aria-label="退款编号"
                                        allowClear
                                        placeholder="请输入退款编号搜索"
                                        value={draftFilters.id}
                                        onChange={event => updateFilter("id", event.target.value)}
                                    />
                                </label>
                                <label className="trade-refund-filter-order">
                                    关联订单
                                    <ZhFilterInput
                                        aria-label="关联订单"
                                        allowClear
                                        placeholder="请输入订单编号搜索"
                                        value={draftFilters.orderId}
                                        onChange={event => updateFilter("orderId", event.target.value)}
                                    />
                                </label>
                                <label className="trade-refund-filter-customer">
                                    采购用户
                                    <ZhFilterInput
                                        aria-label="采购用户"
                                        allowClear
                                        placeholder="请输入采购商账号信息搜索"
                                        value={draftFilters.keyword}
                                        onChange={event => updateFilter("keyword", event.target.value)}
                                    />
                                </label>
                                <label className="trade-refund-filter-date">
                                    创建日期
                                    <ZhDateRangePicker
                                        type="daterange"
                                        format="YYYY-MM-DD"
                                        valueFormat="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="创建开始日期"
                                        endPlaceholder="创建结束日期"
                                        value={[draftFilters.beginDate || null, draftFilters.endDate || null]}
                                        onChange={(_value, formattedValue) => {
                                            updateFilter("beginDate", formattedValue?.[0] || "");
                                            updateFilter("endDate", formattedValue?.[1] || "");
                                        }}
                                    />
                                </label>
                                <label className="trade-refund-filter-state">
                                    状态
                                    <ZhFilterSelect
                                        aria-label="退款状态"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={REFUND_STATE_OPTIONS}
                                        placeholder="请选择状态"
                                        showSearch={false}
                                        classNames={{ popup: { root: "trade-refund-state-dropdown" } }}
                                        value={draftFilters.states}
                                        onChange={value => updateFilter("states", value)}
                                    />
                                </label>
                                <ZhFilterButton
                                    aria-label="查询退款"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </ZhFilterButton>
                                <ZhFilterButton
                                    aria-label="重置退款"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                showSizeChanger
                                total={total}
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default TradeRefundPage;
